<template lang="pug">
div(:class="editorClasses")
  template(v-for="(item, index) in items")
    .divider(v-if="item.type === 'divider'", :key="index")
    MenuItem(v-else, :key="index", v-bind="item")
  v-dialog(
    v-model="dialog",
    max-width="900",
    transition="dialog-bottom-transition"
  )
    v-card.pa-2.pa-sm-8
      .d-flex.align-center.justify-end
        v-btn(icon, @click="dialog = false")
          v-icon(color="textColor") {{ closeIcon }}
      v-card-title.text-h5.pa-4 {{ modalTitle }}
      v-card-text.pa-4
        v-text-field(
          v-model="url",
          v-on:keyup.enter="handleSubmit",
          persistent-placeholder,
          placeholder="Füge einen URL hinzu",
          autofocus,
          filled
        )
          template(v-slot:label)
            span(:style="labelStyles") URL
      v-card-actions.d-flex.flex-column.flex-sm-row
        v-spacer
        v-btn.ma-2(
          color="textColor",
          text,
          x-large,
          @click="removeLink",
          v-if="isLinkActive",
          :block="isXsScreen"
        ) URL entfernen
        Button.ma-2(
          @click="handleSubmit",
          btnLabel="URL speichern",
          :block="isXsScreen"
        )
</template>

<script lang="ts">
import Vue, { PropType } from "vue"
import MenuItem from "./MenuItem.vue"
import Button from "../../UI/Button.vue"
import {
  mdiUndo,
  mdiRedo,
  mdiClose,
  mdiWrap,
  mdiLinkVariant,
  mdiFormatClear,
  mdiFormatQuoteOpen,
  mdiFormatListNumbered,
  mdiFormatBold,
  mdiFormatItalic,
  mdiFormatStrikethrough,
  mdiFormatColorHighlight,
  mdiFormatListBulleted,
} from "@mdi/js"
import { MenuItemObject } from "components/Wysiwyg/menu/MenuItem.vue"

export default Vue.extend({
  components: {
    MenuItem,
    Button,
  },
  props: {
    editor: { type: Object as PropType<any>, required: true },
    inline: { type: Boolean, default: false },
    additionalMenuItems: {
      type: Array as () => MenuItemObject[],
      default: () => [],
    },
  },
  data() {
    return {
      url: null as string | null,
      dialog: false,
      closeIcon: mdiClose,
    }
  },
  computed: {
    items(): MenuItemObject[] {
      const menuItems: MenuItemObject[] = [
        {
          icon: mdiFormatBold,
          title: "Bold",
          action: () => {
            this.editor.chain().focus().toggleBold().run()
          },
          isActive: (): boolean => this.editor.isActive("bold"),
        },
        {
          icon: mdiFormatItalic,
          title: "Italic",
          action: () => {
            this.editor.chain().focus().toggleItalic().run()
          },
          isActive: (): boolean => this.editor.isActive("italic"),
        },
        {
          icon: mdiFormatStrikethrough,
          title: "Strike",
          action: () => {
            this.editor.chain().focus().toggleStrike().run()
          },
          isActive: (): boolean => this.editor.isActive("strike"),
        },
        {
          icon: mdiFormatColorHighlight,
          title: "Highlight",
          action: () => {
            this.editor.chain().focus().toggleHighlight().run()
          },
          isActive: (): boolean => this.editor.isActive("highlight"),
        },
        {
          type: "divider",
        },
        {
          icon: mdiFormatListBulleted,
          title: "Bullet List",
          action: () => {
            this.editor.chain().focus().toggleBulletList().run()
          },
          isActive: (): boolean => this.editor.isActive("bulletList"),
        },
        {
          icon: mdiFormatListNumbered,
          title: "Ordered List",
          action: () => {
            this.editor.chain().focus().toggleOrderedList().run()
          },
          isActive: (): boolean => this.editor.isActive("orderedList"),
        },
        {
          icon: mdiFormatQuoteOpen,
          title: "Blockquote",
          action: () => {
            this.editor.chain().focus().toggleBlockquote().run()
          },
          isActive: (): boolean => this.editor.isActive("blockquote"),
        },
        {
          icon: mdiLinkVariant,
          title: "Link",
          action: () => {
            this.handleModal(true)
          },
          isActive: (): boolean => this.editor.isActive("link"),
        },
        {
          type: "divider",
        },
        {
          icon: mdiWrap,
          title: "Hard Break",
          action: () => {
            this.editor.chain().focus().setHardBreak().run()
          },
        },
        {
          icon: mdiFormatClear,
          title: "Clear Format",
          action: () => {
            this.editor.chain().focus().clearNodes().unsetAllMarks().run()
          },
        },
        {
          icon: mdiUndo,
          title: "Undo",
          action: () => {
            this.editor.chain().focus().undo().run()
          },
        },
        {
          icon: mdiRedo,
          title: "Redo",
          action: () => {
            this.editor.chain().focus().redo().run()
          },
        },
      ]
      if (this.additionalMenuItems.length > 0) {
        menuItems.push({ type: "divider" })
        menuItems.push(...this.additionalMenuItems)
      }
      return menuItems
    },
    isLinkActive(): boolean {
      return this.editor.isActive("link")
    },
    isXsScreen(): boolean {
      return this.$vuetify.breakpoint.name === "xs"
    },
    modalTitle(): string {
      return this.isLinkActive ? "URL bearbeiten" : "URL hinzufügen"
    },
    labelStyles(): object {
      return {
        color: this.$vuetify.theme.themes.light.primary,
        fontWeight: 900,
        fontSize: "1.1em",
      }
    },
    editorClasses(): string {
      return `d-flex flex-wrap align-center justify-start suus__editor__menu ${
        this.inline ? "suus__editor__menu--inline" : ""
      }`
    },
  },
  methods: {
    handleModal(bool: boolean): void {
      this.dialog = bool
    },
    handleSubmit(): void {
      this.editor.chain().focus().setLink({ href: this.url }).run()
      this.dialog = false
    },
    removeLink(): void {
      this.url = null
      this.editor.chain().focus().unsetLink().run()
      this.dialog = false
    },
  },
})
</script>

<style lang="sass" scoped>
$primary-color: rgba(50, 131, 247, 1)
.suus__editor__menu
  background-color: #f5f5f5
  border-radius: 2px
  &--inline
    background-color: #fff
    border: 1px solid #e5e5e5
  &--dark
    background-color: rgba(#f5f5f5, 0.1)
  .divider
    width: 1px
    height: 1.25rem
    background-color: $primary-color
    margin-left: 0.5rem
    margin-right: 0.75rem
    &--dark
      background-color: rgba(#f5f5f5, 0.1)
</style>
